// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useOctoRuleQuery } from "@/api/hooks/use-octo-rules";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { KeyChip } from "@/components/shared/key-chip";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { RequirePermission } from "@/components/shared/require-permission";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { UnsavedChangesDialog } from "@/components/shared/unsaved-changes-dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OctoRuleActionsMenu } from "@/features/octo/octo-rule-actions-menu";
import { OctoRuleActivityTab } from "@/features/octo/octo-rule-activity-tab";
import { OctoOutcomeBadge, OctoStageBadge } from "@/features/octo/octo-rule-badges";
import { OctoRuleEditTab, type OctoRuleEditTabHandle } from "@/features/octo/octo-rule-edit-tab";
import { OctoRuleInfoTab } from "@/features/octo/octo-rule-info-tab";
import { ToggleOctoRuleActiveDialog } from "@/features/octo/toggle-octo-rule-active-dialog";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";

export const Route = createFileRoute("/_authenticated/octo/rules/$ruleId")({
  validateSearch: z.object({ t: z.string().optional(), f: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.octoRules}>
      <OctoRuleDetailPage />
    </RequirePermission>
  ),
});

function OctoRuleDetailPage() {
  const { ruleId } = Route.useParams();
  const { t: tab, f } = Route.useSearch();
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { debugMode } = useDebugMode();
  const canEdit = hasPermission(PAGE_PERMISSIONS.octoRulesEdit);
  const octoRuleId = Number(ruleId);
  const { data: rule, isLoading, isError } = useOctoRuleQuery(octoRuleId);

  const requestedTab = tab ?? "info";
  const tabAllowed: Record<string, boolean> = {
    info: true,
    edit: canEdit,
    // Activity log is work-in-progress (mocked) — debug-mode only.
    activity: debugMode,
  };
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";

  const [isEditDirty, setIsEditDirty] = useState(false);
  const [toggleOpen, setToggleOpen] = useState(false);
  const editTabRef = useRef<OctoRuleEditTabHandle>(null);
  const blocker = useBlocker({
    shouldBlockFn: () => isEditDirty,
    enableBeforeUnload: () => isEditDirty,
    withResolver: true,
  });

  const breadcrumbs = useMemo(() => [{ label: t("octoRules.title"), to: "/octo/rules" }], [t]);

  function handleTabChange(newTab: string) {
    navigate({
      to: "/octo/rules/$ruleId",
      params: { ruleId },
      search: newTab === "info" ? {} : { t: newTab },
      replace: true,
    });
  }

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError || !rule) {
    return (
      <div>
        <PageHeader title={`#${ruleId}`} breadcrumbs={breadcrumbs} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={rule.name || t("octoRules.unnamed", { id: rule.octoRuleId })}
        subtitle={
          <>
            <IdPill id={rule.octoRuleId} />
            {rule.storeName ? (
              <KeyChip label={rule.storeName} variant="store" />
            ) : rule.chainName ? (
              <KeyChip label={rule.chainName} variant="chain" />
            ) : (
              <Badge variant="outline" className="font-mono font-normal lowercase">
                {t("octoRules.scopeGlobal")}
              </Badge>
            )}
            <OctoStageBadge stage={rule.stage} />
            <OctoOutcomeBadge outcome={rule.outcome} />
            <StatusIndicator active={rule.isActive} />
            <OctoRuleActionsMenu
              isActive={rule.isActive}
              onToggleActive={() => setToggleOpen(true)}
              triggerClassName="ml-auto -my-1.5"
            />
          </>
        }
        breadcrumbs={breadcrumbs}
      />
      <div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
          <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
            <TabsList>
              <TabsIndicator />
              <TabsTrigger value="info">{t("octoRules.detail.tabs.info")}</TabsTrigger>
              {canEdit && <TabsTrigger value="edit">{t("octoRules.detail.tabs.edit")}</TabsTrigger>}
              {debugMode && <TabsTrigger value="activity">{t("octoRules.detail.tabs.activity")}</TabsTrigger>}
            </TabsList>
          </div>
          <TabsContent value="info" className="space-y-4">
            <OctoRuleInfoTab rule={rule} />
          </TabsContent>
          {canEdit && (
            <TabsContent value="edit" className="space-y-4">
              <OctoRuleEditTab
                ref={editTabRef}
                octoRuleId={octoRuleId}
                canEdit={canEdit}
                onDirtyChange={setIsEditDirty}
              />
            </TabsContent>
          )}
          {debugMode && (
            <TabsContent value="activity" className="space-y-4">
              <OctoRuleActivityTab octoRuleId={octoRuleId} initialFilters={activeTab === "activity" ? f : undefined} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <ToggleOctoRuleActiveDialog
        rule={
          toggleOpen
            ? {
                octoRuleId: rule.octoRuleId,
                name: rule.name || t("octoRules.unnamed", { id: rule.octoRuleId }),
                isActive: rule.isActive,
              }
            : null
        }
        onClose={() => setToggleOpen(false)}
      />

      <UnsavedChangesDialog
        open={blocker.status === "blocked"}
        onStay={() => blocker.reset?.()}
        onDiscard={() => {
          editTabRef.current?.reset();
          blocker.proceed?.();
        }}
      />
    </div>
  );
}
